// src/components/NotificationsBell.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Divider, CircularProgress } from "@mui/material";
import { FaBell, FaCheck, FaCheckDouble } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const API_BASE = "https://vol-backend.onrender.com";
const POLL_INTERVAL = 60_000; // 60 segundos

// Colores e íconos por tipo
const TYPE_CONFIG = {
  doc_validado:          { color: "#22c55e", label: "Documentos" },
  doc_rechazado:         { color: "#ef4444", label: "Documentos" },
  trayectoria_validada:  { color: "#22c55e", label: "Trayectorias" },
  trayectoria_rechazada: { color: "#ef4444", label: "Trayectorias" },
  entrevista_aprobada:   { color: "#3b82f6", label: "Entrevistas" },
  solicitud_entrevista:  { color: "#3b82f6", label: "Entrevistas" },
  aviso_institucional:   { color: "#f59e0b", label: "Avisos" },
  constancia_emitida:    { color: "#22c55e", label: "Actividades" },
  uploads_pendientes:    { color: "#f59e0b", label: "Progresos" },
  docs_pendientes:       { color: "#f59e0b", label: "Progresos" },
  backlog_nacional:      { color: "#ef4444", label: "Nacional" },
  programa_completado:   { color: "#8b5cf6", label: "Actividades" },
  solicitud_usuario: { color: "#7c3aed", label: "Solicitudes" },
};

async function apiFetch(path, options = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return null;
    const token = await user.getIdToken();
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-firebase-uid": user.uid,
        ...(options.headers || {}),
      },
    });
    if (!res.ok) return null;
    return res.json();
  } catch (_err) {
    return null; // red caída, backend levantando, CORS — todos tratados igual
  }
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "Ahora";
  if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d === 1) return "Ayer";
  if (d < 7)  return `Hace ${d} días`;
  return new Date(dateStr).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}

export default function NotificationsBell({ uid }) {
  const [open, setOpen]          = useState(false);
  const [items, setItems]        = useState([]);
  const [unreadCount, setUnread] = useState(0);
  const [loading, setLoading]    = useState(false);
  const dropRef                  = useRef(null);
  const navigate                 = useNavigate();

  const fetchNotifications = useCallback(async () => {
    const data = await apiFetch("/api/notifications?limit=5&unread_only=false");
    if (!data) return;
    setItems(data.notifications || []);
    setUnread(data.unread_count ?? 0);
  }, []);

  // ── FIX: escuchar onAuthStateChanged en lugar de depender solo de uid como prop
  // Esto garantiza que el fetch se dispara cuando Firebase confirma la sesión,
  // sin importar cuándo llegue uid desde App.js
  useEffect(() => {
    const auth = getAuth();
    let interval = null;
    let retryTimeout = null;
    let started = false; // ← guardia contra múltiples disparos de onAuthStateChanged

    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user || started) return; // ← solo el primer disparo válido arranca el ciclo
      started = true;

      const tryFetch = async () => {
        const data = await apiFetch("/api/notifications?limit=5&unread_only=false");
        if (!data) {
          retryTimeout = setTimeout(tryFetch, 5000); // ahora sí funciona
          return;
        }
        setItems(data.notifications || []);
        setUnread(data.unread_count ?? 0);

        // Ya funcionó — arrancar polling normal
        if (interval) clearInterval(interval);
        interval = setInterval(fetchNotifications, POLL_INTERVAL);
      };

      tryFetch();
    });

    return () => {
      unsub();
      if (interval) clearInterval(interval);
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [fetchNotifications]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    await apiFetch(`/api/notifications/${id}/read`, { method: "PATCH" });
    setItems(prev => prev.map(n => n.notification_id === id ? { ...n, is_read: 1 } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await apiFetch("/api/notifications/read-all", { method: "PATCH" });
    setItems(prev => prev.map(n => ({ ...n, is_read: 1 })));
    setUnread(0);
  };

  const handleVerTodas = () => {
    setOpen(false);
    navigate("/Notificaciones");
  };

  return (
    <Box ref={dropRef} sx={{ position: "relative" }}>
      {/* ── CAMPANA ── */}
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          cursor: "pointer",
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: "50%",
          backgroundColor: open ? "rgba(0,0,0,0.2)" : "transparent",
          transition: "0.2s",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.15)" },
        }}
      >
        <FaBell color="#fff" size={18} />
        {unreadCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 2, right: 2,
              minWidth: 17, height: 17,
              borderRadius: "50%",
              backgroundColor: "#ff3333",
              border: "2px solid #fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.6rem", color: "#fff", fontWeight: 700, lineHeight: 1 }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── DROPDOWN ── */}
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: 46, right: 0,
            width: 340,
            backgroundColor: "#fff",
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            zIndex: 99999,
            overflow: "hidden",
          }}
        >
          {/* Header dropdown */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 2, py: 1.5, backgroundColor: "#f8f8f8", borderBottom: "1px solid #eee"
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#222" }}>
              NOTIFICACIONES
            </Typography>
            <Box
              onClick={handleMarkAllRead}
              sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                cursor: "pointer", color: "#ff3333",
                fontSize: "0.75rem", fontWeight: 600,
                "&:hover": { textDecoration: "underline" }
              }}
            >
              <FaCheckDouble size={11} />
              Ver todas como leídas
            </Box>
          </Box>

          {/* Lista */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} sx={{ color: "#ff3333" }} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <FaBell size={28} color="#ccc" />
              <Typography sx={{ color: "#aaa", fontSize: "0.85rem", mt: 1 }}>
                Sin notificaciones
              </Typography>
            </Box>
          ) : (
            items.map((n) => {
              const cfg = TYPE_CONFIG[n.type] || { color: "#888", label: n.type };
              return (
                <Box
                  key={n.notification_id}
                  sx={{
                    display: "flex", alignItems: "flex-start", gap: 1.5,
                    px: 2, py: 1.5,
                    backgroundColor: n.is_read ? "#fff" : "#fff8f8",
                    borderBottom: "1px solid #f0f0f0",
                    transition: "0.15s",
                    "&:hover": { backgroundColor: "#fef2f2" },
                  }}
                >
                  {/* Dot de color */}
                  <Box sx={{
                    width: 10, height: 10, borderRadius: "50%",
                    backgroundColor: cfg.color, flexShrink: 0, mt: 0.6
                  }} />

                  {/* Texto */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: "0.82rem", fontWeight: n.is_read ? 400 : 700,
                      color: "#222", lineHeight: 1.3,
                      overflow: "hidden", textOverflow: "ellipsis",
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical"
                    }}>
                      {n.titulo}
                    </Typography>
                    <Typography sx={{ fontSize: "0.73rem", color: "#888", mt: 0.3 }}>
                      {timeAgo(n.created_at)}
                    </Typography>
                  </Box>

                  {/* Marcar leída */}
                  {!n.is_read && (
                    <Box
                      onClick={(e) => handleMarkRead(n.notification_id, e)}
                      sx={{
                        cursor: "pointer", color: "#ff3333", flexShrink: 0,
                        "&:hover": { color: "#c00" }, mt: 0.4
                      }}
                      title="Marcar como leída"
                    >
                      <FaCheck size={12} />
                    </Box>
                  )}
                </Box>
              );
            })
          )}

          {/* Footer */}
          <Box
            onClick={handleVerTodas}
            sx={{
              textAlign: "center", py: 1.5,
              cursor: "pointer", backgroundColor: "#f8f8f8",
              borderTop: "1px solid #eee",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
          >
            <Typography sx={{ fontSize: "0.82rem", color: "#ff3333", fontWeight: 600 }}>
              Ver todas las notificaciones →
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}